'use client';

import { usePathname } from 'next/navigation';
import { useTranslation } from 'react-i18next';

import { Breadcrumb } from '@sola/ark-skippy/molecules/breadcrumb/breadcrumb.tsx';
import { keys } from '@sola/contentful-model/keys.ts';
import { getArticlePath } from '@sola/contentful-model/lib/article.ts';
import { articlesUrlBase } from '@sola/contentful-model/lib/routes.ts';
import { LOCALES_CODES } from '@sola/contentful-model/lib/types.ts';
import { ArticleResponseDto } from '@sola/schema/lib/contentful.ts';
import { slashes } from '@sola/utils/url.ts';

import { RenderArticle } from './RenderArticle.tsx';

const ArticleInner = ({
  article,
  locale,
  experiments,
}: {
  article: ArticleResponseDto;
  locale: LOCALES_CODES;
  experiments?: string[];
}) => {
  const data = ArticleResponseDto.parse(article);
  const { t } = useTranslation(['skippy']);

  const path = usePathname();

  const urlBase = data.taxonomyArticleType
    ? articlesUrlBase[data.taxonomyArticleType as keyof typeof articlesUrlBase]
    : '#article';

  // @TODO: Extend the getBreadcrumbFromPage function?
  const breadcrumbs = [
    {
      title: t(`skippy:home`, 'Home'),
      link: `/`,
    },
  ];

  if (data.taxonomyArticleType === keys.articleTypes.pressRelease) {
    breadcrumbs.push({
      title: t(`skippy:pressCenter`, 'Press Center'),
      link: urlBase.split('/').slice(0, -1).join('/'),
    });
  }

  breadcrumbs.push({
    title:
      t(
        `articles:taxonomy.articleTypes.${data.taxonomyArticleType}.pluralName`,
      ) || 'Article',

    link: urlBase,
  });

  breadcrumbs.push({
    title: article.pageTitle || '',
    link: slashes(
      getArticlePath(article.slug, data.taxonomyArticleType),
      'both',
    ),
  });

  return (
    <>
      <Breadcrumb breadcrumbs={breadcrumbs} />
      <main id="main">
        <RenderArticle
          article={data}
          locale={locale}
          path={path}
          experiments={experiments}
        />
      </main>
    </>
  );
};

export const ArticlePage = ({
  article,
  locale,
  experiments,
}: {
  article: ArticleResponseDto;
  locale: LOCALES_CODES;
  experiments?: string[];
}) => {
  return (
    <ArticleInner article={article} locale={locale} experiments={experiments} />
  );
};
